import type { RecruitmentOrigin } from '@/api/imports';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelImport,
  getImport,
  listImports,
  previewImport,
  rollbackImport,
  runImport,
  uploadImport,
  type ImportFilters,
  type ImportSource,
} from '@/api/imports';

export function useImports(filters: ImportFilters) {
  return useQuery({
    queryKey: ['imports', 'list', filters],
    queryFn: () => listImports(filters),
    staleTime: 10_000,
  });
}

export function useImport(id: number | undefined) {
  return useQuery({
    queryKey: ['imports', 'get', id],
    queryFn: () => getImport(id as number),
    enabled: id != null,
    // Poll while RUNNING so the operator sees progress without manual
    // refresh. We stop polling once the row settles on a terminal state.
    refetchInterval: (q) => {
      const data = q.state.data;
      if (
        data &&
        (data.status === 'RUNNING' || data.status === 'PENDING' || data.status === 'ROLLING_BACK')
      )
        return 2_000;
      return false;
    },
  });
}

export function useUploadImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, source }: { file: File; source: ImportSource }) =>
      uploadImport(file, source),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['imports', 'list'] });
    },
  });
}

export function usePreviewImport() {
  return useMutation({
    mutationFn: ({
      id,
      limit = 50,
      sheet,
      mappingId,
    }: {
      id: number;
      limit?: number;
      sheet?: string;
      mappingId?: number;
    }) => previewImport(id, limit, sheet, mappingId),
  });
}

export function useRunImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      sheet,
      recruitmentOrigin,
      recruitmentPartner,
    }: {
      id: number;
      sheet?: string;
      recruitmentOrigin?: RecruitmentOrigin;
      recruitmentPartner?: string;
    }) => runImport(id, sheet, { recruitmentOrigin, recruitmentPartner }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['imports', 'list'] });
      void qc.invalidateQueries({ queryKey: ['imports', 'get', vars.id] });
    },
  });
}

export function useCancelImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) => cancelImport(id),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['imports', 'list'] });
      void qc.invalidateQueries({ queryKey: ['imports', 'get', vars.id] });
    },
  });
}

export function useRollbackImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason, force }: { id: number; reason: string; force?: boolean }) =>
      rollbackImport(id, { reason, force }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['imports', 'list'] });
      void qc.invalidateQueries({ queryKey: ['imports', 'get', vars.id] });
      // Les fiches touchées ont été recalculées ou retirées : tout ce qui
      // affiche des dossiers doit se recharger.
      void qc.invalidateQueries({ queryKey: ['entrepreneurs'] });
    },
  });
}
